import { useUserContext } from "../../Hooks/useUserContext";
import { useRecipeService } from "../../Hooks/useRecipeService";
import { RouterLink } from "../../Shared/RouterLink";
import React, {
	useState,
	useEffect
} from "react";
import {
	Grid,
	Typography,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	Button,
	IconButton,
	Tooltip,
	Paper,
	TextField,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions
} from "@material-ui/core";
import { Spinner } from "../../Shared/Spinner";
import NotLoggedInIcon from "@material-ui/icons/MoodBad";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";

export function TagAdmin(props) {
	const user = useUserContext(props.config);
	const service = useRecipeService(props.config);
	const [items, setItems] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [deletingId, setDeletingId] = useState(null);
	const [showAddDialog, setShowAddDialog] = useState(false);
	const [newTagName, setNewTagName] = useState("");
	const [editingTag, setEditingTag] = useState(null);
	const [editingName, setEditingName] = useState("");

	const hasPermission = user?.isTagAdmin ?? false;

	const sortedItems = [...items].sort((a, b) => {
		const aName = (a?.tagName ?? "").toString().toLocaleLowerCase();
		const bName = (b?.tagName ?? "").toString().toLocaleLowerCase();
		if (aName < bName) return -1;
		if (aName > bName) return 1;
		return 0;
	});

	useEffect(() => {
		document.title = "Tag Admin - " + props.config.appName;
		if (!hasPermission)
			return;

		loadItems();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	function loadItems() {
		setIsLoading(true);
		service.getAllTags(
			(response) => {
				if (response && response.status === 200 && response.data) {
					setItems(response.data);
				}
				setIsLoading(false);
			},
			(error) => {
				console.error(error);
				alert("Failed to load tags");
				setIsLoading(false);
			}
		);
	}

	function handleDelete(event, item) {
		if (event && event.preventDefault)
			event.preventDefault();

		if (!window.confirm(`Delete tag "${item.tagName}"?`))
			return;

		setDeletingId(item.tagId);
		service.deleteTag(item.tagId, () => {
			loadItems();
			setDeletingId(null);
		}, (error) => {
			console.error(error);
			const message = error?.response?.data || "Failed to delete tag";
			alert(message);
			setDeletingId(null);
		});
	}

	function handleAdd() {
		if (!newTagName.trim()) {
			alert("Tag name cannot be empty");
			return;
		}

		service.createTag({ tagName: newTagName.trim() }, (response) => {
			if (response && response.status === 200) {
				loadItems();
				setShowAddDialog(false);
				setNewTagName("");
			} else {
				alert("Failed to add tag");
			}
		}, (error) => {
			console.error(error);
			const conflictMessage = error?.response?.status === 409 ? (error.response?.data?.message || "Tag name already exists") : null;
			const message = conflictMessage || (error.response?.data || error.message || "Failed to add tag");
			alert(message);
		});
	}

	function startEdit(item) {
		setEditingTag(item);
		setEditingName(item.tagName ?? "");
	}

	function closeEditDialog() {
		setEditingTag(null);
		setEditingName("");
	}

	function handleEditSave(merge = false) {
		if (!editingName.trim()) {
			alert("Tag name cannot be empty");
			return;
		}

		service.updateTag(editingTag.tagId, { tagName: editingName.trim() }, merge, (response) => {
			if (response && response.status === 200) {
				loadItems();
				closeEditDialog();
			} else {
				alert("Failed to update tag");
			}
		}, (error) => {
			console.error(error);
			if (error?.response?.status === 409 && error.response?.data?.existingTagId) {
				const existingName = error.response.data.existingTagName || "the other tag";
				if (window.confirm(`A tag named "${existingName}" already exists. Merge this tag into it? This will reassign recipes and delete the current tag.`)) {
					handleEditSave(true);
				}
				return;
			}

			const message = error?.response?.data || error.message || "Failed to update tag";
			alert(message);
		});
	}

	if (!hasPermission)
		return (
			<Grid container spacing={8}>
				<Grid item xs={12}>
					<NotLoggedInIcon fontSize="large" />
					<Typography variant="subtitle1">
						Sorry, you must {user.isLoggedIn ? "" : "be logged in and "}have tag admin permissions to view this page!
					</Typography>
					<Typography style={{ paddingLeft: 40 }} variant="subtitle1">
						Logged in: {user.isLoggedIn ? "True" : "False"}
					</Typography>
					<Typography style={{ paddingTop: 20 }} variant="subtitle1">
						<RouterLink to="/login" >
							{user.isLoggedIn ? "" : "Login Now"}
						</RouterLink>
					</Typography>
				</Grid>
			</Grid>
		);

	return (
		<Grid container spacing={3} style={{ padding: 20 }}>
			<Grid item xs={12}>
				<Typography variant="h4" gutterBottom>
					Tag Administration
				</Typography>
			</Grid>
			<Grid item xs={12} style={{ marginBottom: 12 }}>
				<Button
					variant="contained"
					color="primary"
					onClick={() => setShowAddDialog(true)}
				>
					<AddIcon style={{ marginRight: 10 }} />
					Add Tag
				</Button>
			</Grid>
			<Grid item xs={13}>{/* add +1 to this xs value to fix a display issue */}
				<Paper>
					{isLoading ? (
						<div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
							<Spinner size={24} style={{ marginRight: 10 }} />
							<Typography>Loading...</Typography>
						</div>
					) : (
						<Table className="nowrap-table condensed-table">
							<TableHead>
								<TableRow className="user-admin-header">
									<TableCell align="center"></TableCell>
									<TableCell>Tag Name</TableCell>
									<TableCell align="right">Recipe Count</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{sortedItems.map((item) => (
									<TableRow key={item.tagId}>
										<TableCell align="center">
											<Tooltip title="Edit">
												<IconButton
													color="primary"
													size="small"
													className="rb-icon-btn"
													onClick={() => startEdit(item)}
													aria-label="Edit tag"
												>
													<EditIcon fontSize="small" />
												</IconButton>
											</Tooltip>
											<Tooltip title="Delete">
												<IconButton
													color="primary"
													size="small"
													className="rb-icon-btn"
													onClick={(e) => handleDelete(e, item)}
													disabled={deletingId === item.tagId}
													aria-label="Delete tag"
												>
													<DeleteIcon fontSize="small" />
												</IconButton>
											</Tooltip>
										</TableCell>
										<TableCell>{item.tagName}</TableCell>
										<TableCell align="right">
											{item.recipeCount > 0 ? (
												<a
													href={`/recipes?tagQuery=${encodeURIComponent(item.tagName)}`}
													target="_blank"
													rel="noopener noreferrer"
													style={{ textDecoration: 'underline', color: '#3f51b5' }}
												>
													{item.recipeCount}
												</a>
											) : (
												item.recipeCount
											)}
										</TableCell>
								</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</Paper>
			</Grid>
			<Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Add New Tag</DialogTitle>
				<DialogContent>
					<TextField
						autoFocus
						fullWidth
						label="Tag Name"
						value={newTagName}
						onChange={(e) => setNewTagName(e.target.value)}
						onKeyPress={(e) => {
							if (e.key === 'Enter') {
								handleAdd();
							}
						}}
						margin="normal"
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => { setShowAddDialog(false); setNewTagName(""); }} color="default">
						Cancel
					</Button>
					<Button onClick={handleAdd} color="primary" variant="contained">
						Add
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog open={!!editingTag} onClose={closeEditDialog} maxWidth="sm" fullWidth>
				<DialogTitle>Edit Tag</DialogTitle>
				<DialogContent>
					<TextField
						autoFocus
						fullWidth
						label="Tag Name"
						value={editingName}
						onChange={(e) => setEditingName(e.target.value)}
						onKeyPress={(e) => {
							if (e.key === 'Enter') {
								handleEditSave();
							}
						}}
						margin="normal"
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={closeEditDialog} color="default">
						Cancel
					</Button>
					<Button onClick={() => handleEditSave()} color="primary" variant="contained">
						Save
					</Button>
				</DialogActions>
			</Dialog>
		</Grid>
	);
}
